/**
 * App-wide toast notifications. Reusable across every write page (not price-specific) so
 * future write surfaces get feedback for free. `useToast().notify({...})` pushes a transient
 * message; success/error variants auto-dismiss (errors linger longer). Mounted once at the
 * app root in main.tsx.
 *
 * Full toaster enabled app-wide (the "toaster" option for §7-Q3, pending Tyler). Call sites use
 * `notify(...)`, so if he picks per-page inline banners instead, only this provider's rendering
 * changes — the call sites don't.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import './toast.css';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastInput {
  message: string;
  variant?: ToastVariant;
  /** ms before auto-dismiss; 0 = sticky (caller dismisses). Defaults by variant. */
  duration?: number;
}

interface Toast extends Required<Omit<ToastInput, 'duration'>> {
  id: number;
  duration: number;
}

interface ToastContextValue {
  notify: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 5000,
  error: 8000, // errors linger so the operator can read them
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(
    ({ message, variant = 'info', duration }: ToastInput): number => {
      const id = nextId.current++;
      const ms = duration ?? DEFAULT_DURATION[variant];
      setToasts((prev) => [...prev, { id, message, variant, duration: ms }]);
      if (ms > 0) {
        window.setTimeout(() => dismiss(id), ms);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="region" aria-label="Notifications" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.variant}`} role={t.variant === 'error' ? 'alert' : 'status'}>
            <span className="toast__message">{t.message}</span>
            <button type="button" className="toast__close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
